import React, { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { DatePicker, Button, CheckboxDropdown, HittingMonitor, PERSPECTIVE_TYPES, LoadingSpinner } from 'yankees-react-utils';
import styled from 'styled-components';
import { usePitchVideoFn, useMultiVideoFn } from '../../../data';
import ReportCard from '../../../components/ReportCard';
import { VideoPlayerModal } from '../../../components/VideoPlayer';
import StatTooltip from '../../../components/StatTooltip';

const getLevelOptions = (battingSeasons) => {
    const extra = [{ Abbr: 'All', LevelID: null, SortOrder: 0 }, { Abbr: 'MIN', LevelID: 7, SortOrder: 2 }];

    const levels = battingSeasons.map((bs) => bs.Level);

    return extra.concat([...new Set(levels.map((l) => l.LevelID))].map((levelId) => levels.find((l) => l.LevelID === levelId)))
        .sort((a, b) => a.SortOrder - b.SortOrder)
        .map((level) => ({
            name: level.Abbr,
            value: level.LevelID,
        }));
};

const StyledCheckboxDropdown = styled(CheckboxDropdown)`
    .dropdown-toggle {
        width: 90px;
    }
`;

const MatchupReportHitMon = ({
    hitMonData,
    isHitMonFetching,
    battingSeasons,
    isBattingSeasonsFetching,
    levels,
    startDate,
    endDate,
    onApplyFilters,
    liveFilter,
    setLiveFilter,
    atBatCount,
    getLevelAverages,
    levelAveragesAbortController,
    szTop,
    szBottom,
    vsMePlayerId,
}) => {
    const { t: translate } = useTranslation();
    const appliedLevelOptions = useMemo(() => {
        const options = getLevelOptions(battingSeasons ?? []).map((option) => ({
            ...option,
            checked: option.value === null ? levels.length === 0 : levels.includes(option.value),
        }));
        if (options.some((option) => option.checked)) return options;
        return options.map((option, index) => ({ ...option, checked: index === 0 }));
    }, [battingSeasons, levels]);
    const [levelsChanged, setLevelsChanged] = useState(appliedLevelOptions);
    const [startDateChanged, setStartDateChanged] = useState(startDate);
    const [endDateChanged, setEndDateChanged] = useState(endDate);

    const videoPlayerModalContainer = useRef();
    const pitchVideoFn = usePitchVideoFn();
    const multiVideoFn = useMultiVideoFn();

    useEffect(() => {
        setLevelsChanged(appliedLevelOptions);
        setStartDateChanged(startDate);
        setEndDateChanged(endDate);
    }, [appliedLevelOptions, endDate, startDate]);

    const applyFilter = () => {
        onApplyFilters({
            levels: levelsChanged.filter((level) => level.checked).map((level) => level.value).filter((value) => value !== null),
            startDate: startDateChanged,
            endDate: endDateChanged,
        });
    };

    const onLevelDropdownChange = (o, key, e) => {
        if (key === 'All' && e.target.checked) {
            setLevelsChanged(levelsChanged.map((l, i) => ({
                ...l,
                checked: !i,
            })));
        } else {
            const newLevels = levelsChanged.map((lo) => ({
                ...lo,
                checked: lo.name === o.name ? e.target.checked : !!lo.checked,
            }));
            if (newLevels[0].checked && newLevels.filter((l) => !!l.checked).length > 1) {
                newLevels[0].checked = false;
            } else if (!newLevels.filter((l) => !!l.checked).length) {
                newLevels[0].checked = true;
            }

            setLevelsChanged(newLevels);
        }
    };

    const selectedLevels = levelsChanged.filter((l) => !!l.checked);
    const levelPlaceholder = selectedLevels.length === 1 ? selectedLevels[0].name : `${selectedLevels.length} ${translate('baseball.level_other')}`;

    if (isBattingSeasonsFetching || isHitMonFetching) {
        return <LoadingSpinner />;
    }

    return (
        <>
            <div ref={videoPlayerModalContainer} />
            <ReportCard>
                <div className="d-flex justify-content-between align-items-center mb-2 position-relative" style={{ zIndex: 10 }}>
                    <h4>{translate('sections.opponent.player.hitting-monitor')}</h4>
                    {!window.$IsDugoutApp && (
                        <div className="d-flex align-items-center">
                            <StyledCheckboxDropdown
                                value={levelsChanged}
                                onChange={onLevelDropdownChange}
                                options={levelsChanged}
                                titleOverrideFormat
                                title={levelPlaceholder}
                                className="me-3"
                            />
                            <DatePicker
                                size="sm"
                                maxDate={endDateChanged}
                                value={startDateChanged}
                                onSelect={setStartDateChanged}
                                openToDate={startDateChanged}
                            />
                            <span className="mx-2">{translate('common.to')}</span>
                            <DatePicker
                                size="sm"
                                minDate={startDateChanged}
                                value={endDateChanged}
                                onSelect={setEndDateChanged}
                                openToDate={endDateChanged}
                            />
                            <Button className="ms-2" variant="primary" onClick={applyFilter}>{translate('common.filter')}</Button>
                        </div>
                    )}
                </div>
                <HittingMonitor
                    isLoading={isHitMonFetching}
                    initialPerspective={PERSPECTIVE_TYPES.pitcher}
                    liveFilter={liveFilter}
                    setLiveFilter={setLiveFilter}
                    pitches={hitMonData?.pitches ?? []}
                    pitchVideoFn={pitchVideoFn}
                    multiVideoFn={multiVideoFn}
                    videoPlayerModal={VideoPlayerModal}
                    modalContainer={videoPlayerModalContainer}
                    hidePerspectiveToggle
                    hideEvLaChart
                    hidePitcherHand
                    hideBatterHand
                    maxPAs={atBatCount}
                    enablePlayAllInStatsTable={!window.$IsDugoutApp}
                    showBatTrackingStatsTable
                    hideBatImpactChart={false}
                    hideHitterTimingChart={false}
                    enableLevelAverages={!window.$IsDugoutApp}
                    getLevelAverages={getLevelAverages}
                    levelAveragesAbortController={levelAveragesAbortController}
                    levels={levels}
                    statTooltip={StatTooltip}
                    szTop={szTop}
                    szBottom={szBottom}
                    showPitchSubTypes
                    vsMePlayerId={vsMePlayerId}
                />
            </ReportCard>
        </>
    );
};

MatchupReportHitMon.propTypes = {
    hitMonData: PropTypes.shape({
        pitches: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    isHitMonFetching: PropTypes.bool.isRequired,
    battingSeasons: PropTypes.arrayOf(PropTypes.shape({})),
    isBattingSeasonsFetching: PropTypes.bool.isRequired,
    levels: PropTypes.arrayOf(PropTypes.number).isRequired,
    startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]).isRequired,
    endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]).isRequired,
    onApplyFilters: PropTypes.func.isRequired,
    liveFilter: PropTypes.shape({}).isRequired,
    setLiveFilter: PropTypes.func.isRequired,
    atBatCount: PropTypes.number.isRequired,
    getLevelAverages: PropTypes.func.isRequired,
    levelAveragesAbortController: PropTypes.shape({
        abort: PropTypes.func.isRequired,
    }),
    szTop: PropTypes.number,
    szBottom: PropTypes.number,
    vsMePlayerId: PropTypes.number,
};
MatchupReportHitMon.defaultProps = {
    hitMonData: undefined,
    battingSeasons: [],
    levelAveragesAbortController: undefined,
    szTop: undefined,
    szBottom: undefined,
    vsMePlayerId: undefined,
};

export default MatchupReportHitMon;
